import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, addMonths, isAfter, isBefore } from 'date-fns';
import { createVacationDays, getVacationDays, deleteVacationDay } from '../../services/api';
import type { VacationDayResponse, VacationDayRequest } from '../../services/api';

const VacationDays: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isRange, setIsRange] = useState(false);
  const [description, setDescription] = useState('');
  const [vacationDays, setVacationDays] = useState<VacationDayResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchVacationDays();
  }, []);

  const fetchVacationDays = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const sixMonthsLater = format(addMonths(new Date(), 6), 'yyyy-MM-dd');
      const data = await getVacationDays(today, sixMonthsLater);
      setVacationDays(data);
    } catch (error) {
      setError('Failed to fetch vacation days');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate) return;

    if (isRange && !endDate) {
      setError('Please select an end date');
      return;
    }

    if (isRange && endDate && isBefore(endDate, startDate)) {
      setError('End date must be after start date');
      return;
    }

    const vacationDay: VacationDayRequest = {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: isRange ? format(endDate!, 'yyyy-MM-dd') : format(startDate, 'yyyy-MM-dd'),
      description: description || undefined,
    };

    try {
      setLoading(true);
      await createVacationDays(vacationDay);
      await fetchVacationDays();
      setSuccess('Vacation days added successfully');
      setStartDate(null);
      setEndDate(null);
      setDescription('');
    } catch (error) {
      setError('Failed to add vacation days');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteVacationDay(id);
      setSuccess('Vacation day deleted successfully');
      fetchVacationDays();
    } catch (error) {
      setError('Failed to delete vacation day');
    }
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    if (startDate === endDate) {
      return format(new Date(startDate), 'MMMM d, yyyy');
    }
    return `${format(new Date(startDate), 'MMMM d, yyyy')} - ${format(new Date(endDate), 'MMMM d, yyyy')}`;
  };

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Vacation Days
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRange}
                    onChange={(e) => setIsRange(e.target.checked)}
                  />
                }
                label="Date Range"
              />
            </Grid>
            <Grid item xs={12} md={isRange ? 6 : 12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  disablePast
                />
              </LocalizationProvider>
            </Grid>
            {isRange && (
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    disablePast
                    minDate={startDate || undefined}
                  />
                </LocalizationProvider>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !startDate || (isRange && !endDate)}
              >
                {loading ? 'Adding...' : 'Add Vacation Days'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upcoming Vacation Days
        </Typography>
        <List>
          {vacationDays.map((vacation) => (
            <ListItem key={vacation.id}>
              <ListItemText
                primary={formatDateRange(vacation.start_date, vacation.end_date)}
                secondary={vacation.description}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDelete(vacation.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VacationDays; 